import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHotelBranding } from '../../hooks/useHotelBranding';
import { GDTop, GDSpinnerScreen } from './GDBits';

const ICON_EMOJI: Record<string, string> = {
  wifi: '📶', clock: '🕒', coffee: '☕', key: '🔑', car: '🚗', 'map-pin': '📍',
  phone: '📞', info: 'ℹ️', utensils: '🍽️', moon: '🌙', sparkles: '✨',
  baby: '👶', 'concierge-bell': '🛎️', 'log-out': '🚪',
};

// Same heuristic the legacy screen used: hotels write "Password: X" /
// "Slaptažodis: X" in free text; copy just the token.
const PASSWORD_PATTERNS = [/password[:\s]+(\S+)/i, /slaptažodis[:\s]+(\S+)/i];
const extractPasswordToken = (body: string): string | null => {
  for (const pattern of PASSWORD_PATTERNS) {
    const match = body.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

interface InfoScreenProps {
  isAuthenticated?: boolean;
  fallbackHotelCode?: string;
}

/**
 * Hotel info & house rules — the digital guest book. Public (works straight
 * from the room QR before login) and doubles as the in-stay info screen.
 * All content is hotel-authored sections; nothing here is hardcoded.
 */
const InfoScreen: React.FC<InfoScreenProps> = ({ isAuthenticated, fallbackHotelCode }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set when an expired guest link (24h after checkout) redirected here.
  const linkExpired = searchParams.get('linkExpired') === '1';
  const { hotelInfo, query } = useHotelBranding(fallbackHotelCode);
  const [copied, setCopied] = useState(false);

  const sections = useMemo(() => hotelInfo?.sections || [], [hotelInfo]);
  const wifiSection = useMemo(
    () => sections.find(section =>
      section.icon === 'wifi' || /wi-?fi/i.test(section.slug) || /wi-?fi/i.test(section.title)),
    [sections]
  );
  const wifiPassword = wifiSection ? extractPasswordToken(wifiSection.body) : null;
  const rest = sections.filter(section => section !== wifiSection);
  const groups: Array<{ key: string; items: typeof rest }> = ['essentials', 'stay', 'offers', 'other']
    .map(key => ({ key, items: rest.filter(section => (section.group || 'stay') === key) }))
    .filter(group => group.items.length > 0);

  const hotel = hotelInfo?.hotel;

  if (query.isLoading && !hotelInfo) {
    return <GDSpinnerScreen label={t('gd.common.loading')} />;
  }

  return (
    <section className="gd-screen">
      <GDTop
        title={hotel?.button_labels?.['info'] || t('gd.info.title')}
        onBack={() => navigate(isAuthenticated ? '/home' : '/login')}
      />
      <div className="gd-body">
        {linkExpired && (
          <div className="gd-note" role="alert">
            <p style={{ margin: '0 0 8px' }}>
              {t(
                'gd.info.linkExpired',
                'This booking link has expired — links stop working 24 hours after check-out. You can still browse the hotel information below.'
              )}
            </p>
            <button
              type="button"
              className="gd-btn gd-ghost"
              onClick={() => navigate('/login')}
            >
              {t('gd.info.enterBookingCode', 'Enter a booking code')}
            </button>
          </div>
        )}

        {!hotel && (
          <div className="gd-note">{t('gd.info.noHotel')}</div>
        )}

        {hotel?.description && (
          <p style={{
            margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--gd-ink-2)',
          }}>
            {hotel.description}
          </p>
        )}

        {wifiSection && (
          <div className="gd-wifi">
            <div>
              <small>{t('gd.info.wifi')}</small>
              <b>{wifiSection.title}</b>
              <small style={{ whiteSpace: 'pre-line' }}>{wifiSection.body}</small>
            </div>
            {wifiPassword && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(wifiPassword);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? t('gd.info.copied') : t('gd.info.copyPassword')}
              </button>
            )}
          </div>
        )}

        {hotel && (hotel.check_in_time || hotel.check_out_time) && (
          <details className="gd-fold" open>
            <summary>🕒 {t('gd.info.timings')}</summary>
            <div className="gd-dl">
              {hotel.check_in_time && (
                <div>
                  <span>{t('gd.info.checkInFrom')}</span>
                  <span>{hotel.check_in_time}</span>
                </div>
              )}
              {hotel.check_out_time && (
                <div>
                  <span>{t('gd.info.checkOutBy')}</span>
                  <span>{hotel.check_out_time}</span>
                </div>
              )}
            </div>
          </details>
        )}

        {groups.map(group =>
          group.items.map((section, index) => (
            <details className="gd-fold" key={section.slug} open={group.key === 'essentials' && index === 0}>
              <summary>
                {ICON_EMOJI[section.icon] || 'ℹ️'} {section.title}
              </summary>
              <div className="gd-dl" style={{ whiteSpace: 'pre-line' }}>{section.body}</div>
            </details>
          ))
        )}

        {hotel && (hotel.phone || hotel.email) && (
          <div className="gd-help" style={{ margin: 0 }}>
            <div>
              <b>{t('gd.home.helpTitle')}</b>
              {[hotel.street_address, hotel.city].filter(Boolean).join(', ')}
            </div>
            {hotel.phone && <a href={`tel:${hotel.phone}`}>{t('gd.home.call')}</a>}
          </div>
        )}

        {!isAuthenticated && (
          <button type="button" className="gd-btn" onClick={() => navigate('/login')}>
            {t('gd.info.haveBooking')}
          </button>
        )}

        {(hotelInfo?.available_languages?.length || 0) > 1 && (
          <div className="gd-chips" style={{ justifyContent: 'center' }}>
            {hotelInfo!.available_languages.map(lang => (
              <button
                key={lang}
                type="button"
                className={`gd-chip${i18n.language.startsWith(lang) ? ' gd-on' : ''}`}
                onClick={() => i18n.changeLanguage(lang)}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        <div className="gd-powered">
          {t('gd.home.poweredBy')} <b>FrontDesko</b>
        </div>
      </div>
    </section>
  );
};

export default InfoScreen;
